import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Rating,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import FeedbackService from '../services/FeedbackService';
import { usePostHog } from '@posthog/react';

const MAX_FILES = 5;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

// Survey definitions for each direction
const SURVEY = {
  // Company is rating a Jobseeker
  jobseeker: {
    title: 'Rate this worker',
    questions: [
      { key: 'q1', label: 'Reliability', helper: 'Did they show up on time and as agreed?' },
      { key: 'q2', label: 'Quality of work', helper: 'Did they deliver good work?' },
      { key: 'q3', label: 'Professionalism', helper: 'Were they respectful and easy to work with?' },
    ],
    incidentTypes: [
      { value: 'no_show', label: 'No show' },
      { value: 'disrespect', label: 'Disrespect / inappropriate behaviour' },
      { value: 'other', label: 'Other serious issue' },
    ],
    placeholder: 'Share your experience working with them (optional, recommended)',
  },
  // Jobseeker is rating a Company
  company: {
    title: 'Rate this company',
    questions: [
      { key: 'q1', label: 'Payment', helper: 'Were you paid correctly and on time?' },
      { key: 'q2', label: 'Working conditions', helper: 'Was the workplace safe and well-organised?' },
      { key: 'q3', label: 'Communication & respect', helper: 'Was the company clear and respectful?' },
    ],
    incidentTypes: [
      { value: 'no_payment', label: 'No payment / underpayment' },
      { value: 'disrespect', label: 'Disrespect / unsafe conditions' },
      { value: 'other', label: 'Other serious issue' },
    ],
    placeholder: 'Share your experience working with them (optional, recommended)',
  },
};

/**
 * Unified feedback dialog used in both directions.
 *
 * Required props:
 *  - open, onClose
 *  - ratedProfileType: 'jobseeker' | 'company'
 *  - ratedProfileId
 *  - raterProfileType, raterProfileId, raterName
 *
 * Optional:
 *  - ratedName (display)
 *  - jobId, applicationId (context)
 *  - initialIncidentMode (boolean) — open the dialog in incident mode
 *  - onSubmitted(feedback) — callback after success
 */
const FeedbackDialog = ({
  open,
  onClose,
  ratedProfileType,
  ratedProfileId,
  ratedName,
  raterProfileType,
  raterProfileId,
  raterName,
  jobId,
  applicationId,
  initialIncidentMode = false,
  onSubmitted,
}) => {
  const posthog = usePostHog();
  const survey = SURVEY[ratedProfileType] || SURVEY.jobseeker;

  const [ratings, setRatings] = useState({ q1: 0, q2: 0, q3: 0 });
  const [comment, setComment] = useState('');
  const [incidentMode, setIncidentMode] = useState(initialIncidentMode);
  const [incidentType, setIncidentType] = useState('');
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [confirmedWorked, setConfirmedWorked] = useState(false);

  // Whether this is a jobseeker-rating-company flow
  const isJobseekerRatingCompany = raterProfileType === 'jobseeker' && ratedProfileType === 'company';

  // Reset state whenever the dialog opens
  useEffect(() => {
    if (open) {
      setRatings({ q1: 0, q2: 0, q3: 0 });
      setComment('');
      setIncidentMode(initialIncidentMode);
      setIncidentType('');
      setFiles([]);
      setError('');
      setSuccess(false);
      setConfirmedWorked(false);
    }
  }, [open, initialIncidentMode]);

  const allRatingsSet = ratings.q1 > 0 && ratings.q2 > 0 && ratings.q3 > 0;

  const canSubmit = useMemo(() => {
    if (isJobseekerRatingCompany && !confirmedWorked) return false;
    if (incidentMode) {
      return (
        !!incidentType &&
        comment.trim().length >= 20 &&
        files.length > 0 &&
        allRatingsSet
      );
    }
    return allRatingsSet;
  }, [incidentMode, incidentType, comment, files, allRatingsSet, isJobseekerRatingCompany, confirmedWorked]);

  const reset = () => {
    setRatings({ q1: 0, q2: 0, q3: 0 });
    setComment('');
    setIncidentMode(initialIncidentMode);
    setIncidentType('');
    setFiles([]);
    setError('');
    setSuccess(false);
    setConfirmedWorked(false);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose?.();
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files || []);
    if (files.length + newFiles.length > MAX_FILES) {
      setError(`Maximum ${MAX_FILES} files allowed`);
      return;
    }
    for (const f of newFiles) {
      if (f.size > MAX_FILE_SIZE) {
        setError(`File "${f.name}" exceeds 10 MB`);
        return;
      }
    }
    setError('');
    setFiles((prev) => [...prev, ...newFiles]);
    e.target.value = '';
  };

  const handleRemoveFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSetIncidentMode = (next) => {
    setIncidentMode(next);
    if (next) {
      // Pre-fill ratings to 1 to reflect a serious negative experience
      setRatings({ q1: 1, q2: 1, q3: 1 });
    } else {
      setIncidentType('');
      setFiles([]);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const payload = {
        ratedProfileId,
        ratedProfileType,
        raterProfileId,
        raterProfileType,
        raterName,
        jobId,
        applicationId,
        ratings: { q1: ratings.q1, q2: ratings.q2, q3: ratings.q3 },
        comment: comment.trim() || undefined,
        incidentType: incidentMode ? incidentType : undefined,
      };
      const created = await FeedbackService.submitWithEvidence(
        payload,
        incidentMode ? files : [],
      );
      posthog?.capture('feedback_submitted', {
        rated_profile_type: ratedProfileType,
        rater_profile_type: raterProfileType,
        is_incident: incidentMode,
        incident_type: incidentMode ? incidentType : undefined,
      });
      setSuccess(true);
      onSubmitted?.(created);
    } catch (err) {
      console.error('Feedback submission failed:', err);
      const message =
        err?.body?.error ||
        err?.body?.message ||
        err?.message ||
        'Failed to submit feedback. Please try again.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={700}>
            {survey.title}
          </Typography>
          <IconButton onClick={handleClose} disabled={submitting} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {success ? (
          <Alert severity="success" sx={{ my: 2 }}>
            Thank you for your feedback. {incidentMode ? 'Our team will review your report shortly.' : 'It helps build a trusted community.'}
          </Alert>
        ) : (
          <Stack spacing={3} sx={{ mt: 1 }}>
            {ratedName && (
              <Typography variant="body2" color="text.secondary">
                You are rating <strong>{ratedName}</strong>.
              </Typography>
            )}

            {/* Confirmation gate for jobseekers rating companies */}
            {isJobseekerRatingCompany && (
              <Alert severity="info" variant="outlined">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={confirmedWorked}
                      onChange={(e) => setConfirmedWorked(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      I confirm that I have worked on this job and my feedback is based on my personal experience.
                    </Typography>
                  }
                />
              </Alert>
            )}

            {/* Survey questions */}
            <Stack spacing={2}>
              {survey.questions.map((q) => (
                <Box key={q.key}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    {q.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {q.helper}
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Rating
                      value={ratings[q.key]}
                      onChange={(_, v) => setRatings((r) => ({ ...r, [q.key]: v || 0 }))}
                      icon={<StarIcon fontSize="inherit" />}
                      emptyIcon={<StarIcon fontSize="inherit" />}
                      size="large"
                    />
                  </Box>
                </Box>
              ))}
            </Stack>

            {/* Optional comment */}
            <TextField
              label={incidentMode ? 'Describe what happened (required, min 20 chars)' : 'Comment / testimonial'}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              fullWidth
              multiline
              rows={3}
              placeholder={survey.placeholder}
              required={incidentMode}
              helperText={incidentMode ? `${comment.length}/20 minimum` : undefined}
            />

            {/* Incident toggle */}
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={incidentMode}
                    onChange={(e) => handleSetIncidentMode(e.target.checked)}
                    color="error"
                  />
                }
                label={
                  <Typography variant="body2">
                    Report a <strong>serious incident</strong> (no-show, no-payment, etc.)
                  </Typography>
                }
              />
              {incidentMode && (
                <Stack spacing={2} sx={{ mt: 1 }}>
                  <TextField
                    select
                    label="Incident type"
                    value={incidentType}
                    onChange={(e) => setIncidentType(e.target.value)}
                    fullWidth
                    required
                  >
                    {survey.incidentTypes.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>

                  <Box>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<AttachFileIcon />}
                      disabled={files.length >= MAX_FILES}
                    >
                      Attach evidence ({files.length}/{MAX_FILES})
                      <input
                        type="file"
                        hidden
                        multiple
                        onChange={handleFileChange}
                        accept="image/*,.pdf,.doc,.docx"
                      />
                    </Button>
                    {files.length > 0 && (
                      <List dense sx={{ mt: 1 }}>
                        {files.map((f, i) => (
                          <ListItem key={i} disableGutters>
                            <ListItemText
                              primary={f.name}
                              secondary={`${(f.size / 1024).toFixed(1)} KB`}
                            />
                            <ListItemSecondaryAction>
                              <IconButton size="small" onClick={() => handleRemoveFile(i)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </Box>

                  <Alert severity="warning" variant="outlined">
                    Serious incident reports are reviewed by our team. False reports may result in account suspension.
                  </Alert>
                </Stack>
              )}
            </Box>

            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        {success ? (
          <Button onClick={handleClose} variant="contained">Done</Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              color={incidentMode ? 'error' : 'primary'}
              disabled={submitting || !canSubmit}
              startIcon={submitting ? <CircularProgress size={16} /> : null}
            >
              {submitting ? 'Submitting…' : incidentMode ? 'Submit Report' : 'Submit Feedback'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default FeedbackDialog;
